import { useEffect, useRef, useMemo, useContext, memo, Suspense } from 'react';
import { Routes, useLocation, UNSAFE_LocationContext } from 'react-router-dom';
import { useTabs, getScrollFor, TabActiveContext, TabActiveRefContext, consumeTabSwitchNavForShell } from '../context/TabsContext';
import { StaffLayoutChromeImpl, StaffShellContext } from './DashboardLayout';
import StaffRoutesList from '../routes/StaffRoutesList';

const TabFallback = () => (
  <div className="min-h-[40vh] w-full flex items-center justify-center">
    <div className="w-8 h-8 rounded-full border-2 border-forest-600 border-t-transparent animate-spin" />
  </div>
);

// One kept-alive tab. Memoised so that switching tabs only re-renders the two
// frames whose `isActive` flips — the other N hidden page trees (with their
// hundreds of <Route> elements each) are left untouched, which is what makes
// a tab switch cost ~one page render instead of N.
const sameLocation = (a, b) => a && b && a.pathname === b.pathname && a.search === b.search && a.hash === b.hash && a.state === b.state;

const TabFrame = memo(function TabFrame({ path, isActive, routerCtx }) {
  // <Routes> reads the ROUTER's LocationContext (useRoutesImpl → useLocation), and every page
  // inside reads it too (useLocation/useSearchParams/useNavigate). So the frame owns its own
  // LocationContext value and only ever swaps it when THIS tab is active and its URL actually
  // changed. Hidden tabs never see a new value (no re-render of their whole page tree per
  // navigation), and switching back to a tab whose URL hasn't changed re-renders nothing below
  // <Routes> — the page is simply shown again.
  const ctxRef = useRef(null);
  if (isActive && routerCtx && !sameLocation(ctxRef.current?.location, routerCtx.location)) ctxRef.current = routerCtx;
  if (!ctxRef.current) ctxRef.current = { location: { pathname: path, search: '', hash: '', state: null, key: path }, navigationType: 'POP' };
  const ctx = ctxRef.current;
  // Cheap, re-render-free "am I active?" for hooks that only need it inside callbacks.
  const activeRef = useRef({ current: isActive });
  activeRef.current.current = isActive;
  // Route elements built once per frame so a <Routes> re-render passes the SAME page element
  // down and React bails out of the page itself.
  const routes = useMemo(() => StaffRoutesList(), []);
  // Tabs restored from a previous session stay EMPTY until first shown — otherwise a reload with
  // 6-8 saved tabs mounts every page at once and fires all their API calls together (slow first
  // paint on phones / the 1-vCPU server). Once shown, a tab is kept alive like before.
  const everActiveRef = useRef(isActive);
  if (isActive) everActiveRef.current = true;
  if (!everActiveRef.current) return <div className="h-full min-h-0" style={{ display: 'none' }} data-testid={`tab-frame-${path}`} />;
  return (
    <div className="h-full min-h-0" style={{ display: isActive ? undefined : 'none' }} data-testid={`tab-frame-${path}`}>
      <UNSAFE_LocationContext.Provider value={ctx}>
        <TabActiveRefContext.Provider value={activeRef.current}>
          <TabActiveContext.Provider value={isActive}>
            {/* Per-tab Suspense — CRITICAL: must NOT be a single boundary wrapping
                every tab, or the first visit to any lazy chunk unmounts every other
                open tab's kept-alive DOM while that chunk loads. */}
            <Suspense fallback={<TabFallback />}>
              <Routes location={ctx.location}>
                {routes}
              </Routes>
            </Suspense>
          </TabActiveContext.Provider>
        </TabActiveRefContext.Provider>
      </UNSAFE_LocationContext.Provider>
    </div>
  );
});

// True per-tab freeze: every open tab's page stays fully mounted (just
// display:none while inactive) so switching tabs and back resumes exactly
// where the user left off — scroll, half-typed forms, open filters — like a
// real browser tab, instead of the page unmounting/remounting on every nav.
//
// The chrome (sidebar, header, tab bar, global search) renders exactly ONCE
// here, wrapping N per-tab content trees. Each individual staff page still
// calls <StaffLayout> internally (unchanged, 70+ files) — StaffShellContext
// makes that a no-op passthrough so the chrome never duplicates per tab.
export default function StaffTabKeepAliveShell() {
  const { tabs } = useTabs();
  const location = useLocation();
  const routerCtx = useContext(UNSAFE_LocationContext);

  // Guarantees the CURRENTLY active path always renders even before
  // TabsContext's own effect (in HeaderTabsBar) has added it — e.g. the very
  // first navigation right after login, before localStorage has any tabs.
  const activePath = location.pathname;
  const renderList = tabs.some((t) => t.path === activePath)
    ? tabs
    : [...tabs, { path: activePath, title: activePath }];

  // Per-tab scroll memory. Window scroll is a single, document-wide value
  // shared by every mounted tab (only the active one is display:block at a
  // time), so it has to be saved/restored explicitly per tab path — the
  // browser does NOT remember a separate scrollY per hidden subtree. The
  // save half lives in TabsContext.markTabSwitchNav (called synchronously
  // by every "switch tabs" action, BEFORE navigate() runs) — this effect
  // only owns the restore half.
  //
  // Gated on consumeTabSwitchNavForShell(): only a genuine tab switch
  // (sidebar click, tab-strip click, minimised-chip restore, global search
  // jump) should force this tab's remembered scroll. A normal in-tab
  // navigation — list → detail → native Back — must NOT be touched here;
  // that flow is owned end-to-end by ScrollRestoration.js (sessionStorage
  // keyed by pathname, restored only on POP). Forcing scrollTo(0, 0) here
  // unconditionally on every activePath change previously clobbered that
  // restore for every list page in the app (Customers, Loans Hub, etc).
  const prevPathRef = useRef(null);
  useEffect(() => {
    if (prevPathRef.current !== null && prevPathRef.current !== activePath && consumeTabSwitchNavForShell()) {
      const target = getScrollFor(activePath);
      window.scrollTo(0, target);
      // One follow-up frame in case the tab-switch itself (sidebar closing,
      // header re-layout) causes a reflow right after the first scrollTo.
      requestAnimationFrame(() => window.scrollTo(0, target));
    }
    prevPathRef.current = activePath;
  }, [activePath]);

  return (
    <StaffShellContext.Provider value={true}>
      <StaffLayoutChromeImpl>
        {renderList.map((tab) => {
          const isActive = tab.path === activePath;
          return <TabFrame key={tab.path} path={tab.path} isActive={isActive} routerCtx={isActive ? routerCtx : null} />;
        })}
      </StaffLayoutChromeImpl>
    </StaffShellContext.Provider>
  );
}
